import 'what-input';

import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import * as Pages from '$pages';
import { Grid } from '$partials/development/index.js';
import { LocaleProvider } from '$contexts/locale.jsx';
import { SkipLink } from '$partials/ui/index.js';
import { setLocation } from '$utilities/location.js';
import { setSiteName } from '$utilities/site-name.js';
import { setUserAgent } from '$utilities/user-agent.js';
import { useQueryParamsInSession } from '$utilities/storage.js';
import { setRoutes } from '$utilities/routes';
import ContentfulLivePreviewOverlay from '$partials/global/contentful-live-preview';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component App
 * @props (see propTypes)
 *
 * Renders the application.
 * 1. If the page component requested doesn’t exist, escapes the function.
 * 2. Skip link are useful for keyboard users to access the main content of the page
 *    straight away without the hassle of navigating through the navigation at each page load.
 * 3. Page component (HomePage, UnionPage, etc.).
 */
const App = props => {
    useQueryParamsInSession(['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']);

    if (props.location) {
        setLocation(props.location);
    }
    if (props.routes) {
        setRoutes(props.routes);
    }
    if (props.userAgent) {
        setUserAgent(props.userAgent);
    }
    if (props.siteName) {
        setSiteName(props.siteName);
    }

    useEffect(() => {
        // put some user data in to session storage for use with HubSpot later
        if (window && window.document && window.document.cookie) {
            // HubSpot user token
            const keys = window.document.cookie.split('; ');
            keys.forEach(key => {
                if (key.includes('hubspotutk')) {
                    const [k, v] = key.split('=');
                    window.sessionStorage.setItem('hubspot_hubspotutk', v);
                }
            });
        }
    }, []);

    /* [1] */
    if (!props.pageComponent || typeof (Pages[props.pageComponent]) === 'undefined') {
        return null;
    }

    const Component = Pages[props.pageComponent];

    return (
        <LocaleProvider
            current={props.locale}
            isDev={props.dev}
            options={props.localeOptions}
            translations={props.data?.translations}>
            <ContentfulLivePreviewOverlay
                data={props.data}
                preview={props.preview}>
                <SkipLink href='#main-content'>Skip to main content</SkipLink> {/* [3] */}
                <SkipLink href='#primary-navigation'>Skip to primary navigation</SkipLink> {/* [3] */}
                <Component {...props} /> {/* [3] */}
                {(!!props.dev) && <Grid />}
            </ContentfulLivePreviewOverlay>
        </LocaleProvider>
    );
};

/* ========================================================================= */
/* Typing & export(s) */
/* ========================================================================= */

App.propTypes = {
    data: PropTypes.object,
    dev: PropTypes.bool,
    locale: PropTypes.object.isRequired,
    localeOptions: PropTypes.array,
    location: PropTypes.string,
    pageComponent: PropTypes.string.isRequired,
    siteName: PropTypes.string,
    timezone: PropTypes.string,
    userAgent: PropTypes.object.isRequired
};

export { App };

export default App;
